// Recording utilities using RecordRTC
import RecordRTC from "recordrtc";

interface RecordingState {
  isRecording: boolean;
  startTime: number | null;
  recorder: RecordRTC | null;
  stream: MediaStream | null;
  isMobileRecording: boolean;
}

class RecordingManager {
  private static instance: RecordingManager | null = null;
  private recordingState: RecordingState = {
    isRecording: false,
    startTime: null,
    recorder: null,
    stream: null,
    isMobileRecording: false,
  };

  static getInstance(): RecordingManager {
    if (!RecordingManager.instance) {
      RecordingManager.instance = new RecordingManager();
    }

    return RecordingManager.instance;
  }

  private constructor() {}

  /**
   * Check if the current device is mobile
   */
  private isMobileDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform;
    const maxTouchPoints = navigator.maxTouchPoints;
    const isSecureContext = window.isSecureContext;

    // Enhanced mobile detection for Brave on Android
    const isMobile =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
      (platform === "MacIntel" && maxTouchPoints > 1) ||
      // Additional checks for Brave on Android
      (userAgent.includes("brave") && userAgent.includes("android")) ||
      (userAgent.includes("chrome") && userAgent.includes("android") && maxTouchPoints > 0) ||
      // Check for touch capabilities
      "ontouchstart" in window ||
      maxTouchPoints > 0;

    console.log("=== Mobile Device Detection ===");
    console.log(`User Agent: ${userAgent}`);
    console.log(`Platform: ${platform}`);
    console.log(`Max Touch Points: ${maxTouchPoints}`);
    console.log(`Is Secure Context: ${isSecureContext}`);
    console.log(`Has Touch Events: ${"ontouchstart" in window}`);
    console.log(
      `Is Brave on Android: ${userAgent.includes("brave") && userAgent.includes("android")}`,
    );
    console.log(
      `Is Chrome on Android: ${userAgent.includes("chrome") && userAgent.includes("android")}`,
    );
    console.log(`Detected as Mobile: ${isMobile}`);
    console.log("=== End Mobile Device Detection ===");

    return isMobile;
  }

  /**
   * Check if RecordRTC library is properly loaded
   */
  private isRecordRTCLoaded(): boolean {
    try {
      // Test if RecordRTC is available and can be instantiated
      if (typeof RecordRTC === "undefined") {
        console.error("RecordRTC library is not loaded");

        return false;
      }

      console.log("RecordRTC library is properly loaded");

      return true;
    } catch (error) {
      console.error("RecordRTC library test failed:", error);

      return false;
    }
  }

  /**
   * Check if recording is supported in the current browser
   */
  isRecordingSupported(): boolean {
    const isMobile = this.isMobileDevice();
    const hasMediaDevices = typeof navigator.mediaDevices !== "undefined";
    const hasGetUserMedia = typeof navigator.mediaDevices?.getUserMedia === "function";
    const hasGetDisplayMedia = typeof navigator.mediaDevices?.getDisplayMedia === "function";
    const hasRecordRTC = this.isRecordRTCLoaded();
    const isSecureContext = window.isSecureContext;

    console.log("=== Recording Support Detection ===");
    console.log(`Is Mobile: ${isMobile}`);
    console.log(`Has MediaDevices: ${hasMediaDevices}`);
    console.log(`Has getUserMedia: ${hasGetUserMedia}`);
    console.log(`Has getDisplayMedia: ${hasGetDisplayMedia}`);
    console.log(`Has RecordRTC: ${hasRecordRTC}`);
    console.log(`Is Secure Context: ${isSecureContext}`);

    // For mobile, we only need microphone access and RecordRTC
    if (isMobile) {
      const mobileSupported = hasMediaDevices && hasGetUserMedia && hasRecordRTC && isSecureContext;

      console.log(`Mobile Recording Supported: ${mobileSupported}`);
      console.log("=== End Recording Support Detection ===");

      return mobileSupported;
    }

    // For desktop, we need screen recording support
    const desktopSupported =
      hasMediaDevices && hasGetDisplayMedia && hasRecordRTC && isSecureContext;

    console.log(`Desktop Recording Supported: ${desktopSupported}`);
    console.log("=== End Recording Support Detection ===");

    return desktopSupported;
  }

  /**
   * Check if mobile recording is supported (audio-only)
   */
  isMobileRecordingSupported(): boolean {
    const hasMediaDevices = typeof navigator.mediaDevices !== "undefined";
    const hasGetUserMedia = typeof navigator.mediaDevices?.getUserMedia === "function";
    const hasRecordRTC = this.isRecordRTCLoaded();
    const isSecureContext = window.isSecureContext;

    const supported = hasMediaDevices && hasGetUserMedia && hasRecordRTC && isSecureContext;

    console.log("=== Mobile Recording Support Detection ===");
    console.log(`Has MediaDevices: ${hasMediaDevices}`);
    console.log(`Has getUserMedia: ${hasGetUserMedia}`);
    console.log(`Has RecordRTC: ${hasRecordRTC}`);
    console.log(`Is Secure Context: ${isSecureContext}`);
    console.log(`Mobile Recording Supported: ${supported}`);
    console.log("=== End Mobile Recording Support Detection ===");

    return supported;
  }

  /**
   * Start recording - screen recording for desktop, audio-only for mobile
   */
  async startRecording(): Promise<MediaStream> {
    if (!this.isRecordingSupported()) {
      throw new Error("Recording not supported in this browser");
    }

    if (this.recordingState.isRecording) {
      throw new Error("Recording already in progress");
    }

    const isMobile = this.isMobileDevice();

    try {
      if (isMobile) {
        return await this.startMobileRecording();
      } else {
        return await this.startDesktopRecording();
      }
    } catch (error) {
      console.error("Failed to start recording:", error);
      throw error;
    }
  }

  /**
   * Start audio-only recording for mobile devices
   */
  private async startMobileRecording(): Promise<MediaStream> {
    console.log("Starting mobile audio-only recording");

    try {
      // Get microphone audio stream with fallback options
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // Add fallback options for Brave on Android
          sampleRate: 44100,
          channelCount: 1,
        },
        video: false,
      });

      console.log("Microphone stream obtained:", {
        trackCount: micStream.getTracks().length,
        audioTracks: micStream.getAudioTracks().length,
        videoTracks: micStream.getVideoTracks().length,
      });

      // Create RecordRTC instance for audio-only recording with fallback options
      const recorder = new RecordRTC(micStream, {
        type: "audio",
        mimeType: "audio/webm",
        disableLogs: false,
        timeSlice: 1000,
        ondataavailable: (blob: Blob) => {
          console.log("Mobile recording data available:", blob.size, "bytes");
        },
        audioBitsPerSecond: 128000, // 128 kbps
        // Add fallback options for better compatibility
        numberOfAudioChannels: 1,
        desiredSampRate: 44100,
      });

      this.recordingState = {
        isRecording: true,
        startTime: Date.now(),
        recorder,
        stream: micStream,
        isMobileRecording: true,
      };

      // Start recording
      recorder.startRecording();

      console.log("Mobile audio recording started with RecordRTC:", {
        type: "audio",
        mimeType: "audio/webm",
        hasMicrophone: true,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
      });

      return micStream;
    } catch (error) {
      console.error("Failed to start mobile recording:", error);

      // Try with simpler audio constraints as fallback
      try {
        console.log("Trying with simpler audio constraints...");
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: true, // Use default audio constraints
          video: false,
        });

        const recorder = new RecordRTC(micStream, {
          type: "audio",
          mimeType: "audio/webm",
          disableLogs: false,
          timeSlice: 1000,
          ondataavailable: (blob: Blob) => {
            console.log("Mobile recording data available (fallback):", blob.size, "bytes");
          },
          audioBitsPerSecond: 128000,
        });

        this.recordingState = {
          isRecording: true,
          startTime: Date.now(),
          recorder,
          stream: micStream,
          isMobileRecording: true,
        };

        recorder.startRecording();

        console.log("Mobile audio recording started with fallback settings");

        return micStream;
      } catch (fallbackError) {
        console.error("Failed to start mobile recording with fallback:", fallbackError);
        throw fallbackError;
      }
    }
  }

  /**
   * Start full screen recording for desktop devices
   */
  private async startDesktopRecording(): Promise<MediaStream> {
    console.log("Starting desktop screen recording");

    // Get screen stream with audio
    const screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: 1920,
        height: 1080,
        frameRate: 30,
      },
      audio: {
        //@ts-ignore
        suppressLocalAudioPlayback: false,
      },
      preferCurrentTab: true,
      systemAudio: "include",
    });

    // Get microphone audio stream
    let micStream: MediaStream | null = null;

    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
    } catch (error) {
      console.warn("Could not access microphone:", error);
    }

    // Combine screen and microphone audio
    let combinedStream = screenStream;

    if (micStream) {
      const audioContext = new AudioContext();
      const screenAudioSource = audioContext.createMediaStreamSource(screenStream);
      const micAudioSource = audioContext.createMediaStreamSource(micStream);
      const destination = audioContext.createMediaStreamDestination();

      // Create gain nodes to control volume
      const screenGain = audioContext.createGain();
      const micGain = audioContext.createGain();

      // Set gain levels
      screenGain.gain.value = 0.7; // Page audio at 70%
      micGain.gain.value = 0.8; // Microphone at 80%

      // Connect audio sources to gain nodes
      screenAudioSource.connect(screenGain);
      micAudioSource.connect(micGain);

      // Connect gain nodes to destination
      screenGain.connect(destination);
      micGain.connect(destination);

      // Create combined stream with video from screen and mixed audio
      const videoTracks = screenStream.getVideoTracks();
      const audioTracks = destination.stream.getAudioTracks();

      combinedStream = new MediaStream([...videoTracks, ...audioTracks]);
    }

    // Create RecordRTC instance with standard configuration
    const recorder = new RecordRTC(combinedStream, {
      type: "video",
      mimeType: "video/webm;codecs=vp8",
      disableLogs: false,
      timeSlice: 1000,
      ondataavailable: (blob: Blob) => {
        console.log("Desktop recording data available:", blob.size, "bytes");
      },
      videoBitsPerSecond: 2500000, // 2.5 Mbps
      audioBitsPerSecond: 128000, // 128 kbps
    });

    this.recordingState = {
      isRecording: true,
      startTime: Date.now(),
      recorder,
      stream: combinedStream,
      isMobileRecording: false,
    };

    // Start recording
    recorder.startRecording();

    console.log("Desktop recording started with RecordRTC:", {
      type: "video",
      mimeType: "video/webm;codecs=vp8",
      frameRate: 30,
      hasMicrophone: !!micStream,
      hasPageAudio: true,
    });

    return combinedStream;
  }

  /**
   * Stop recording and return the blob
   */
  async stopRecording(): Promise<Blob | null> {
    if (!this.recordingState.isRecording || !this.recordingState.recorder) {
      throw new Error("No recording in progress");
    }

    return new Promise((resolve, reject) => {
      const { recorder, stream, isMobileRecording } = this.recordingState;

      if (!recorder) {
        reject(new Error("Recorder is null"));

        return;
      }

      recorder.onStateChanged = (state: string) => {
        console.log("RecordRTC state changed:", state);
      };

      recorder.stopRecording(() => {
        try {
          const blob = recorder.getBlob();

          console.log(
            `${isMobileRecording ? "Mobile" : "Desktop"} recording stopped, blob size:`,
            blob.size,
            "bytes",
          );

          // Stop all tracks
          if (stream) {
            stream.getTracks().forEach((track) => track.stop());
          }

          // Reset state
          this.recordingState = {
            isRecording: false,
            startTime: null,
            recorder: null,
            stream: null,
            isMobileRecording: false,
          };

          resolve(blob);
        } catch (error) {
          console.error("Error getting recording blob:", error);
          reject(error);
        }
      });
    });
  }

  /**
   * Get current recording state
   */
  getRecordingState(): RecordingState {
    return { ...this.recordingState };
  }

  /**
   * Check if currently recording
   */
  isRecording(): boolean {
    return this.recordingState.isRecording;
  }

  /**
   * Check if currently recording on mobile
   */
  isMobileRecording(): boolean {
    return this.recordingState.isMobileRecording;
  }

  /**
   * Get recording duration in milliseconds
   */
  getRecordingDuration(): number {
    if (!this.recordingState.isRecording || !this.recordingState.startTime) {
      return 0;
    }

    return Date.now() - this.recordingState.startTime;
  }

  /**
   * Format duration as MM:SS
   */
  formatDuration(durationMs: number): string {
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  /**
   * Download the recording blob
   */
  downloadRecording(blob: Blob, filename: string = "recording"): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.style.display = "none";
    a.href = url;

    // Determine file extension based on recording type
    const isMobile = this.isMobileDevice();
    const extension = isMobile ? "webm" : "webm"; // Both use webm but different content

    a.download = `${filename}.${extension}`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log("Recording downloaded:", filename);
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    if (this.recordingState.recorder) {
      try {
        this.recordingState.recorder.destroy();
      } catch (error) {
        console.error("Error destroying recorder:", error);
      }
    }

    if (this.recordingState.stream) {
      this.recordingState.stream.getTracks().forEach((track) => track.stop());
    }

    this.recordingState = {
      isRecording: false,
      startTime: null,
      recorder: null,
      stream: null,
      isMobileRecording: false,
    };
  }
}

export const getRecordingManager = () => RecordingManager.getInstance();
